/**
 * My Images Page
 * Upload/manage images used as QR code logos or redirect destinations
 */
import React, { useEffect, useRef, useState } from 'react';
import { Image as ImageIcon, Upload, Trash2, Pencil, Loader2, Check, X, QrCode } from 'lucide-react';
import toast from 'react-hot-toast';
import { DashboardLayout } from '../../components/dashboard/DashboardLayout';
import { Button } from '../../components/Button';
import { imagesApi } from '../../api/services';
import type { ImageItem } from '../../types/image.types';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function MyImagesPage() {
  const [images, setImages] = useState<ImageItem[]>([]);
  const [imageCount, setImageCount] = useState(0);
  const [imageLimit, setImageLimit] = useState(0);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [renamingId, setRenamingId] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [lightboxImage, setLightboxImage] = useState<ImageItem | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!lightboxImage) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxImage(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxImage]);

  useEffect(() => {
    loadImages();
  }, []);

  const loadImages = async () => {
    try {
      const response = await imagesApi.listImages({ page_size: 100 });
      setImages(response.images);
      setImageCount(response.image_count);
      setImageLimit(response.image_limit);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to load images');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file next time
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be 2MB or smaller');
      return;
    }

    setUploading(true);
    try {
      const uploaded = await imagesApi.uploadImage(file);
      setImages((prev) => [uploaded, ...prev]);
      setImageCount((prev) => prev + 1);
      toast.success('Image uploaded');
    } catch (error: any) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 403) {
        toast.error(detail || 'Image limit reached. Please upgrade your subscription to upload more images.');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to upload image');
      }
    } finally {
      setUploading(false);
    }
  };

  const startRename = (image: ImageItem) => {
    setRenamingId(image.id);
    setRenameValue(image.filename);
  };

  const cancelRename = () => {
    setRenamingId(null);
    setRenameValue('');
  };

  const submitRename = async (id: number) => {
    const trimmed = renameValue.trim();
    if (!trimmed) {
      toast.error('Filename cannot be blank');
      return;
    }
    try {
      const updated = await imagesApi.renameImage(id, trimmed);
      setImages((prev) => prev.map((img) => (img.id === id ? updated : img)));
      cancelRename();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to rename image');
    }
  };

  const handleDelete = async (id: number) => {
    setDeletingId(id);
    try {
      await imagesApi.deleteImage(id);
      setImages((prev) => prev.filter((img) => img.id !== id));
      setImageCount((prev) => Math.max(0, prev - 1));
      setDeleteConfirmId(null);
      toast.success('Image deleted');
    } catch (error: any) {
      if (error.response?.status === 409) {
        const usedBy = error.response.data?.detail?.used_by || [];
        const names = usedBy.map((u: any) => u.title).join(', ');
        toast.error(
          names
            ? `This image is used by: ${names}. Remove it from those first.`
            : 'This image is still in use by a QR code. Remove it from there first.'
        );
      } else {
        toast.error(error.response?.data?.detail || 'Failed to delete image');
      }
    } finally {
      setDeletingId(null);
    }
  };

  const quotaReached = imageLimit > 0 && imageCount >= imageLimit;
  const quotaPercent = imageLimit > 0 ? Math.min(100, (imageCount / imageLimit) * 100) : 0;

  return (
    <DashboardLayout currentPage="images">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="heading-1 mb-1">My Images</h1>
            <p className="text-[--clinqr-text-secondary]">
              Upload images to use as QR code logos or redirect destinations
            </p>
          </div>
          <Button onClick={() => fileInputRef.current?.click()} disabled={uploading || quotaReached}>
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Upload Image
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={handleFileSelected}
          />
        </div>

        {/* Quota bar */}
        <div className="bg-white rounded-xl border border-[--clinqr-border] p-4 mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-[--clinqr-text-primary]">
              {imageCount} / {imageLimit} images used
            </span>
            {quotaReached && (
              <a
                href="/dashboard/billing"
                className="text-sm font-medium text-[--clinqr-primary] hover:underline"
              >
                Upgrade for more images
              </a>
            )}
          </div>
          <div className="w-full h-2 bg-[--clinqr-bg-subtle] rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${quotaReached ? 'bg-red-500' : 'bg-[--clinqr-primary]'}`}
              style={{ width: `${quotaPercent}%` }}
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[--clinqr-primary]" />
          </div>
        ) : images.length === 0 ? (
          <div className="bg-white rounded-xl border border-[--clinqr-border] p-12 text-center">
            <ImageIcon className="w-12 h-12 text-[--clinqr-text-muted] mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[--clinqr-text-primary] mb-2">No images yet</h3>
            <p className="text-[--clinqr-text-secondary] mb-4">
              Upload your first image to use as a QR code logo or redirect destination
            </p>
            <Button onClick={() => fileInputRef.current?.click()} className="mx-auto">
              <Upload className="w-4 h-4" />
              Upload Image
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {images.map((image) => (
              <div
                key={image.id}
                className="bg-white rounded-xl border border-[--clinqr-border] overflow-hidden hover:border-[--clinqr-text-muted] transition-colors"
              >
                <button
                  type="button"
                  onClick={() => setLightboxImage(image)}
                  className="w-full aspect-square bg-[--clinqr-bg-subtle] flex items-center justify-center overflow-hidden cursor-zoom-in"
                  title="View full size"
                >
                  <img src={image.url} alt={image.filename} className="w-full h-full object-contain" />
                </button>
                <div className="p-3">
                  {renamingId === image.id ? (
                    <div className="flex items-center gap-1">
                      <input
                        autoFocus
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') submitRename(image.id);
                          if (e.key === 'Escape') cancelRename();
                        }}
                        className="w-full px-2 py-1 text-sm border border-[--clinqr-border] rounded focus:outline-none focus:ring-2 focus:ring-[--clinqr-primary]/20"
                      />
                      <button
                        onClick={() => submitRename(image.id)}
                        className="p-1 text-green-600 hover:bg-green-50 rounded"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <button onClick={cancelRename} className="p-1 text-[--clinqr-text-secondary] hover:bg-gray-100 rounded">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <p className="text-sm font-medium text-[--clinqr-text-primary] truncate" title={image.filename}>
                      {image.filename}
                    </p>
                  )}
                  <p className="text-xs text-[--clinqr-text-secondary] mt-0.5">{formatBytes(image.size_bytes)}</p>

                  {image.used_by_count > 0 && (
                    <div className="flex items-center gap-1 mt-2 text-xs text-[--clinqr-text-secondary]">
                      <QrCode className="w-3 h-3" />
                      Used by {image.used_by_count} QR code{image.used_by_count === 1 ? '' : 's'}
                    </div>
                  )}

                  <div className="flex items-center gap-2 mt-3">
                    {renamingId !== image.id && (
                      <button
                        onClick={() => startRename(image)}
                        className="p-1.5 text-[--clinqr-text-secondary] hover:text-[--clinqr-primary] hover:bg-[--clinqr-bg-subtle] rounded transition-colors"
                        title="Rename"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                    {deleteConfirmId === image.id ? (
                      <div className="flex items-center gap-1 ml-auto">
                        <Button variant="secondary" size="sm" onClick={() => setDeleteConfirmId(null)}>
                          Cancel
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDelete(image.id)}
                          disabled={deletingId === image.id}
                        >
                          {deletingId === image.id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm'}
                        </Button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setDeleteConfirmId(image.id)}
                        className="p-1.5 text-[--clinqr-text-secondary] hover:text-red-600 hover:bg-red-50 rounded transition-colors ml-auto"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {lightboxImage && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-colors"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
          <div className="max-w-4xl max-h-[85vh] flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
            <img
              src={lightboxImage.url}
              alt={lightboxImage.filename}
              className="max-w-full max-h-[75vh] object-contain rounded-lg shadow-2xl"
            />
            <p className="text-sm text-white/80">{lightboxImage.filename}</p>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
